import "./AKTable.scss";
import "react-datepicker/dist/react-datepicker.css";

import {
  CButton,
  CDataTable,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";

import AKCell from "./AKCell";
import AKHeader from "./AKHeader";
import AKPaginationButtons from "./AKPaginationButtons";
import AKSearchBox from "./AKSearchBox";
import CIcon from "@coreui/icons-react";
import FormInput from "../form/inputs/FormInput";
import InfiniteScroller from "../infinite-scroller/InfiniteScroller";
import Loading from "../Loading";
import { allDivisions } from "../../../constants/constants";
import { divisionTypes } from "../../../constants/division-types";
import { inputTypes } from "../../../constants/input-types";
import { optionSortMethods } from "../../../constants/optionSortMethods";

/**
 * Reusable Table component for All Kind Painting & Cleaning.
 * @param {Object} root
 * The root component object.
 *
 * @param {Object[]} root.data (Required)
 * Array of objects to be rendered in the table
 * Each object in the array represents one row of the table
 *
 * @param {function} root.setData (Required)
 * Accessor function to manipulate the current data state
 *
 * @param {function} root.setOptions (Optional)
 * If this function is passed, then a button will display to handle adjusting visible columns on the table.
 * If set, default columns must have a default property set to true in the options array to reset to original state.
 * To handle table width, pass minWidth conditionally based on number of columns set to display: true.
 *
 * @param {Object[{header: string, key: string, sortMethod: string | (isAscending: boolean, a: any, b: any) => number, show: boolean, hideFilterOption: boolean, precision: int, type: string}]} root.options (Required)
 * Array of objects with properties; header (Required), key (Required),
 * sortMethod (Required if onSortClick is undefined)
 * and show (True to show column, defaults to false)
 *
 * header: String to be displayed as title of table column;
 *
 * key: Property name for cells to be rendered in this column of the table;
 *
 * sortMethod: string of values "string", "number", "boolean" or "date" for sorting the
 * table on the column's associated property. If defined the onSortClick will be ignored.
 *
 * show: Boolean to determine whether or not to display the object property as a column in the table. Defaults to false.
 *
 * default: Boolean that is used to determine that the column is a default. Used when setOptions is passed
 * as part of the column visibility.
 *
 * hideFilterOption: Boolean to determine whether or not to hide the object property in the filter modal.
 * Booleans are ignored for searching and are a good example of a property to hide.
 *
 * precision: For numerical cells to set precision to a fixed value if passed in
 *
 * @param {string} root.orderKey (Required)
 * Value of the key to order the table on. Table will manage the state by default, the parent component simply needs to house
 * the variable
 *
 * @param {function} root.setOrderKey (Required)
 * Setter function for the orderKey variable. Table will manage the state by default, the parent component simply needs to house
 * the state
 * management hook
 *
 * @param {boolean} root.isAscending (Required)
 * Value of whether to sort isAscending or descending. Table will manage the state by default,
 * the parent component simply needs to house the variable
 *
 * @param {function} root.setIsAscending (Required)
 * Setter function for the isAscending variable. Table will manage the state by default, the parent component simply needs to house
 * the state
 * management hook
 *
 * @param {string=} root.defaultOrderKey (Optional)
 * Key to sort by when data first arrives. Applied once; afterwards the user controls sort state.
 *
 * @param {boolean=} root.defaultIsAscending (Optional)
 * Sort direction to use alongside defaultOrderKey. Defaults to true (ascending) if omitted.
 *
 * @param {boolean=} root.includeInactive
 * Value of whether to include items that have the property isActive set to false.
 *
 * @param {function=} root.setIncludeInactive
 * Setter function for updating the includeInactive boolean.
 *
 * @param {boolean=} root.includePending
 * Value of whether to include items that have the property isPending set to true.
 *
 * @param {function=} root.setIncludePending
 * Setter function for updating the includePending boolean.
 *
 * @param {function=} root.onSortClick
 * Click handler function for custom sorting a column. Defined function must accept parameters
 * (isAscending: boolean, key: string) and handle sorting both isAscending
 * and descending appropriately
 *
 * @param {function=} root.onSearchClick (Optional) Requires that queryString and searchKeys state managed in parent component
 * Search handler function for searching for DB resources and recreating the table
 * Defined function must accept parameters (queryString: string, keys: string[])
 *
 * queryString: The string user entered into the searchbox;
 *
 * keys: The string array of what object keys to filter the search on
 *
 * @param {number=} root.page (Optional) Allows pagination:
 * Integer holding the value of the current page state. setPage and numberOfPages are required if page is defined.
 *
 * @param {function=} root.setPage (Optional) Allows pagination:
 * Accessor function to manipulate the current page state. page and numberOfPages are required if setPage is defined.
 *
 * @param {number=} root.numberOfPages (Optional) Allows pagination:
 * Integer holding the value of the total number of pages. page and setPage are required if numberOfPages is defined.
 * (Parent component controls number of rows per page and must calculate and pass in total pages as prop)
 *
 * @param {function=} root.onAddClick (Optional)
 * Add handler function for creating new resources. If defined an add button will render above the table.
 * Defined function is passed no parameters.
 *
 * @param {function=} root.onImportClick (Optional)
 * Import handler function for creating new resources. If defined an import button will render above the table.
 * Defined function is passed no parameters.
 *
 * @param {object[]=} root.customLineButtons (Optional)
 * An array of objects {buttonLabel, onClick, minWidth} that will append to the last column after edit / delete if they exist.
 * Defined handler function must accept paramater (item: Object)
 *
 * buttonLabel: Display name on the button.
 * onClick: Click handler for the button.
 * minWidth: Minimum width of the button in rem.
 * hideFor: Array of objects {key:string  val:string} key is the column to check, val is the value to hide for
 * if any of the values of the current row for the columns (key) matches val, the button won't be rendered.
 *
 * showFor: Array of objects {key: string val:string} key is column to check, val is the value to show for
 * if any of the values of the current row for the columns key don't match val, the button won't be rendered.
 *
 * Returns:
 * item: The anonymous object pertaining to the clicked row.
 *
 * @param {boolean=} root.alwaysShowLineButtons (Optional)
 * If true line buttons will always display, even with selectable rows.
 *
 * @param {object[]=} root.lineButtonKeyValuesToHide (Optional)
 * An array of objects {key, val}. If customLineButtons exist, pass this array of key value pairs to hide buttons for some rows
 * any keys will be checked for val, if a match is found on that row buttons won't render.
 *
 * key: the column name to check
 * val: the column value to check
 *
 * @param {string=} root.queryString (Optional) Required if onSearchClick is defined
 * Value of the string to query on. Table will manage the state by default, the parent component simply needs to house the variable
 *
 * @param {function=} root.setQueryString (Optional) Required if onSearchClick is defined
 * Setter function for the queryString variable. Table will manage the state by default, the parent component simply needs to house
 * the state
 * management hook
 *
 * @param {string[]=} root.searchKeys (Optional) Required if onSearchClick is defined
 * Value of the keys to query on. Table will manage the state by default, the parent component simply needs to house the variable
 *
 * @param {function=} root.setSearchKeys (Optional) Required if onSearchClick is defined
 * Setter function for the searchKeys variable. Table will manage the state by default, the parent component simply needs to house
 * the state
 * management hook
 *
 * @param {(item: Object, index: number) => void=} root.onRowClick
 * Callback for clicking on a row. Defined callback function must accept parameter (item: Object, index: Number)
 *
 * @param {boolean=} root.selectableRows (Optional) Required if onRowClick is defined. State to manage the selectable row checkbox.
 *
 * @param {boolean=} root.setSelectableRows (Optional) Required if onRowClick is defined. State handler to manage the selectable row checkbox state.
 *
 * @param {string=} root.rowSelectionTitle (Optional) Overrides the default row selection title of "Enable Row Selection"
 *
 * @param {boolean=} root.selectedRows (Optional) Required if onRowClick is defined. Array of the selected rows
 * (needs an id property) so that selected color style classes can be applied.
 *
 * @param {array=} root.forceSelectedRowUpdates (Optional) An array of keys. The keys must be defined in the selected rows. If they are the values of the table for the key columns
 * will be forced to the value set in the selected rows.
 *
 * * @param {boolean=} root.deselectedRows (Optional) requires forceSelectedRowUpdates to be defined. Set these when a deselected row requires being cleared to a specific value.
 * the deselected row entry should include the keys defined in forceSelectedRowUpdates as well as what their value should be set to on deselection.
 *
 * @param {function=} root.onInlineEdit (Optional)
 * Callback for saving inline cell changes to a back-end. Function must accept parameters:
 *
 * value: The current value of the input
 *
 * item: The item object pertaining to the entire row that the edited cell resides in
 *
 * key: The key name of the particular cell being edited
 *
 * event: The event triggered from on blur
 *
 * @param {function=} root.onInlineValidate (Optional)
 * Callback for validation of inline input. Function must accept parameters:
 *
 * value: The current value of the input
 *
 * key: The key name of the particular cell being edited
 *
 * @param {function=} root.onInlineFormat (Optional)
 * Callback for formatting of inline input. Function must accept parameters:
 *
 * event: The event
 *
 * key: The key name of the particular cell being edited
 *
 * @param {function=} root.onCellClick (Optional)
 * Callback for setting notifying parent component which cell was clicked.
 * Currently this is used for changing what inlineSelections are passed into the AKTable for dropdown menus
 * Function must accept parameter:
 *
 * item: The item object pertaining to the entire row that the cell resides in.
 *
 * key: The key name of the particular cell being edited
 *
 * @param {function=} root.setInlineSelections (Optional)
 * Callback function for changing the inline selections. One half of the returned value from a useState call for inline selections
 *
 * @param {string[]=} root.inlineSelections (Optional)
 * The string array to maintain what options are selectable for any given select component in the inline table
 *
 * @param {string[]=} root.ignoreInlineKeys (Optional)
 * The string array containing keys that should not be allowed to be edited
 *
 * @param {string[]=} root.customInlineKeys (Optional)
 * The string array containing keys that should exhibit custom behavior during inline editing
 * Currently this is used to indicate what cells are selectable inputs instead of string inputs
 *
 * @param {string=} root.minWidth (Optional)
 * Overrides the min width set to 772px. Must be in string representation of width in order to be valid
 *
 * @param {string=} root.maxHeight (Optional)
 * Overrides the max height set to 600px. Must be in string representation of width in order to be valid
 *
 * @param {boolean=} root.disabled (Optional)
 * Optionally renders the table with disabled color properties. Does not prevent editing of inline cells or button clicks.
 *
 * @param {string=} root.title (Optional)
 * Optionally renders a title centered and above the AKTable
 *
 * @param {string=} root.titleClassName (Optional)
 * Adds optional className styling to the title prop
 *
 * @param {boolean=} root.forceRerender (Optional)
 * Boolean to force each individual AKCell to rerender if updates to state in a parent component aren't configured to rerender the entire table automatically
 * Useful when state is managed and updated without an API fetch call to repopulate state
 *
 * @param {function=} root.setForceRerender (Optional) Required when forceRerender is passed
 * Set state method to update the forceRerender boolean to false after a cell has rerendered
 *
 * @param {boolean=} root.disableSorting (Optional)
 * Optionally disables the clickable header sorting capabilities and prevents rendering the arrow to indicate sorted order
 *
 * @param {function=} root.handleShouldEmphasizeCell (Optional)
 * Method to determine if a cell should be emphasized by bolding its content text passed in by parent component. Method should accept
 * (item, key) as parameters where item is the object pertaining to the row a cell resides in and key is the property pertaining to the particular cell
 *
 * @param {(item: Object, key: string) => boolean=} root.handleShouldLockCell (Optional)
 * Method to determine if a cell should be locked for editing passed in by parent component. Method should accept
 * (item, key) as parameters where item is the object pertaining to the row a cell resides in and key is the property pertaining to the particular cell
 *
 * @param {Object[]=} root.lockedCells (Optional)
 * An array of objects with properties index and key { index: , key: } where index is the row index and key is the property name associated with the cell
 * to be locked
 *
 * @param {Object[]=} root.emphasizedCells (Optional)
 * An array of objects with properties index and key { index: , key: } where index is the row index and key is the property name associated with the cell
 * to be emphasized by bolding its content text
 *
 * @param {int[]=} root.lockedRows (Optional)
 * An array of integer indexes to lock an entire row for editing
 *
 * @param {boolean=} root.inlineCellValidation (Optional)
 * Boolean to determine whether to highlight invalid cells. Requires the validCells prop to have an array of valid cells that will not be highlighted
 *
 * @param {Object[]=} root.validCells (Optional) Required when inlineCellValidation is true
 * An array of objects with properties index and key { index: , key: } where index is the row index and key is the property name associated with the cell.
 * Used for inline editing to determine what cells should be highlighted as invalid and which cells should not.
 *
 * @param {boolean=} root.hideRowSelectionToggle (Optional)
 * Hides the checkbox for row selection when required row selection props have been passed into the AKTable.
 *
 * @param {string=} root.divisionNameForTheme (Optional)
 * Pass in a division name to change the table theme.
 *
 * @param {boolean=} root.colorRowByDivision (Optional)
 * Set to true if the row has a division key and you want to color the row based on the division.
 *
 * @param {boolean=} root.loading (Optional)
 * If true, styles table so user knows we're in loading state, this way we don't have to hide the entire table while loading.
 *
 * @param {boolean=} root.useLightAndDark (Optional)
 * If true, table rows will alternate between light and dark, otherwise they will all be the dark color.
 *
 * @param {function=} root.handleExportDataClick (Optional)
 * If defined, table header will display a button to export data.
 *
 * @param {string=} root.noResultsMessage (Optional)
 * If defined, table will render this message when no results are found.
 *
 * ///Infinite Scrolling Logic///
 *
 * @param {boolean=} root.useInfiniteScroll (Optional)
 * Boolean to utilize infinite scrolling for batching API calls as a user scrolls the table
 *
 * @param {int=} root.numItems (Optional) Required when useInfiniteScroll is true
 * The integer number of total items/rows that will be in the table assuming no batching occurs
 *
 * @param {int=} root.rowHeight (Optional) Required when useInfiniteScroll is true
 * The height of a table row in pixels to be used for determining current index
 *
 * @param {int=} root.tableHeight (Optional) Required when useInfiniteScroll is true
 * The height of the table in pixels
 *
 * @param {int=} root.batchSize (Optional) Required when useInfiniteScroll is true
 * The integer size of each batch to fetch as a user scrolls down the table
 *
 * @param {int=} root.fetchBuffer (Optional) Required when useInfiniteScroll is true
 * If integer buffer size to act as a threshold where if the scrolled index passes this threshold the handleFetchData method will be called
 *
 * @param {function=} root.handleFetchData (Optional) Required when useInfiniteScroll is true
 * The method to fetch individual batches of data to render in the table that accepts as parameters (skip: {int}, take: {int})
 * Where skip is the number of rows to skip and take is the batch size
 *
 * @param {boolean=} root.shouldResetInfiniteScrollingState (Optional) Required when useInfiniteScroll is true
 * Boolean flag to trigger a state reset within the infinite scrolling component
 *
 * @param {function=} root.setShouldResetInfiniteScrollingState (Optional) Required when useInfiniteScroll is true
 * Setter function to reset the should reset state for the infinite scrolling component
 *
 * @param {string=} root.datatableContainerClassName (Optional) set additional class names on the datatable container
 * 
 * @param {function=} root.getRowClass (Optional) Function to set classNames on rows based on row data.
 * Function should accept parameter item and return a string of classNames to apply to the row.
 * 
 * @param {boolean=} root.wrapCustomButtons (Optional) If true buttons will wrap with one per line.
 * @returns
 */
const AKTable = ({
  data,
  setData,
  options,
  setOptions,
  page,
  setPage,
  numberOfPages,
  includeInactive,
  setIncludeInactive,
  includePending,
  setIncludePending,
  selectableRows,
  setSelectableRows,
  selectedRows,
  forceSelectedRowUpdates,
  workDaysRows,
  deselectedRows,
  onRowClick,
  onSortClick,
  onAddClick,
  onImportClick,
  customHeaderButton,
  customLineButtons,
  alwaysShowLineButtons,
  onSearchClick,
  queryString,
  setQueryString,
  searchKeys,
  setSearchKeys,
  orderKey,
  setOrderKey,
  isAscending,
  setIsAscending,
  defaultOrderKey,
  defaultIsAscending = true,
  previousOrderKey,
  setPreviousOrderKey,
  previousIsAscending,
  setPreviousIsAscending,
  rowSelectionTitle,
  onInlineEdit,
  onInlineValidate,
  onInlineFormat,
  onCellClick,
  onEmailSetInactivesClick,
  ignoreInlineKeys,
  customInlineKeys,
  inlineSelections,
  setInlineSelections,
  minWidth,
  maxHeight,
  disabled,
  hideRowSelectionToggle,
  inlineCellValidation,
  validCells,
  lockedRows,
  lockedCells,
  emphasizedCells,
  handleShouldLockCell,
  handleShouldEmphasizeCell,
  disableSorting,
  forceRerender,
  setForceRerender,
  title,
  titleClassName,
  divisionNameForTheme,
  colorRowByDivision,
  loading,
  handleExportDataClick,
  useLightAndDark = true,
  overrideDivisionKey,
  noResultsMessage,
  sessionFilters,
  employeeManagement,
  // Infinite Scrolling Props
  useInfiniteScroll,
  numItems,
  rowHeight,
  tableHeight,
  batchSize,
  fetchBuffer,
  handleFetchData,
  shouldResetInfiniteScrollingState,
  setShouldResetInfiniteScrollingState,
  sessionStorageUniqueId,
  datatableContainerClassName,
  getRowClass,
  wrapCustomButtons,
  ...cellProps
}) => {
  const ref = useRef();
  // Style class states
  const [showColumnModal, setShowColumnModal] = useState(false);

  const [sessionLoaded, setSessionLoaded] = useState(false);
  const defaultSortApplied = useRef(false);
  const internalUpdate = useRef(false);

  /**
   * Use Effect Hooks
   */

  /**
   * Applies initial sort when data first arrives, then re-applies the active
   * sort on every subsequent external data refresh (e.g. after a save).
   * Uses internalUpdate ref to skip data changes caused by our own setData
   * calls inside handleSort so we don't create an infinite loop.
   */
  useEffect(() => {
    if (!data?.length) return;

    if (internalUpdate.current) {
      internalUpdate.current = false;
      return;
    }

    if (!defaultSortApplied.current) {
      defaultSortApplied.current = true;
      const initialKey = defaultOrderKey ?? orderKey;
      const initialAscending = defaultOrderKey ? defaultIsAscending : isAscending;
      if (initialKey) {
        const option = options?.find((o) => o.key === initialKey);
        if (option?.sortMethod) {
          handleSort(option, initialAscending);
        } else if (defaultOrderKey) {
          setOrderKey(defaultOrderKey);
          setIsAscending(defaultIsAscending);
        }
      }
      return;
    }

    // Re-apply current sort after an external data refresh
    if (orderKey) {
      const option = options?.find((o) => o.key === orderKey && o.sortMethod);
      if (option) {
        handleSort(option, isAscending);
      }
    }
  }, [data]);

  /**
   * Effect to create/fetch sorting and filtering data from session storage
   */
  useEffect(() => {
    let isMounted = true;

    if (sessionStorageUniqueId) {
      if (!sessionLoaded) {
        const isAscendingFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:isAscending`,
        );
        const orderKeyFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:orderKey`,
        );
        const previousIsAscendingFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:previousIsAscending`,
        );
        const previousOrderKeyFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:previousOrderKey`,
        );
        const searchKeysFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:searchKeys`,
        );
        const queryStringFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:queryString`,
        );
        const pageFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:page`,
        );

        const includeInactiveFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:includeInactive`,
        );

        const includePendingFromStorage = sessionStorage.getItem(
          `${sessionStorageUniqueId}:includePending`,
        );

        if (isAscendingFromStorage != null) {
          isMounted && setIsAscending(JSON.parse(isAscendingFromStorage));
        }

        if (orderKeyFromStorage != null) {
          isMounted && setOrderKey(orderKeyFromStorage);
        }

        if (previousIsAscendingFromStorage != null) {
          isMounted &&
            setPreviousIsAscending(JSON.parse(previousIsAscendingFromStorage));
        }

        if (previousOrderKeyFromStorage != null) {
          isMounted && setPreviousOrderKey(previousOrderKeyFromStorage);
        }

        if (searchKeysFromStorage != null) {
          const storageSearchKeys = JSON.parse(searchKeysFromStorage);
          if (searchKeys) {
            for (let i = 0; i < searchKeys.length; i++) {
              if (searchKeys[i] !== storageSearchKeys[i]) {
                isMounted && setSearchKeys(storageSearchKeys);
                break;
              }
            }
          } else {
            isMounted && setSearchKeys(storageSearchKeys);
          }
        }

        if (queryStringFromStorage != null) {
          isMounted && setQueryString(queryStringFromStorage);
        }

        if (pageFromStorage != null) {
          isMounted && setPage(JSON.parse(pageFromStorage));
        }

        if (includeInactiveFromStorage != null) {
          isMounted &&
            setIncludeInactive(JSON.parse(includeInactiveFromStorage));
        }

        if (includePendingFromStorage != null) {
          isMounted && setIncludePending(JSON.parse(includePendingFromStorage));
        }

        if (sessionFilters != null) {
          // Fetch filters external to core AKTable functionality
          for (const sessionFilter of sessionFilters) {
            const state = sessionStorage.getItem(
              `${sessionStorageUniqueId}:${sessionFilter.key}`,
            );

            if (state != null) {
              isMounted && sessionFilter.setState(JSON.parse(state));
            } else if (sessionFilter.default != null) {
              isMounted && sessionFilter.setState(sessionFilter.default);
            }
          }
        }

        isMounted && setSessionLoaded(true);
      } else {
        if (isAscending != null) {
          isMounted &&
            sessionStorage.setItem(
              `${sessionStorageUniqueId}:isAscending`,
              JSON.stringify(isAscending),
            );
        }

        if (orderKey != null) {
          isMounted &&
            sessionStorage.setItem(
              `${sessionStorageUniqueId}:orderKey`,
              orderKey,
            );
        }

        if (searchKeys != null) {
          isMounted &&
            sessionStorage.setItem(
              `${sessionStorageUniqueId}:searchKeys`,
              JSON.stringify(searchKeys),
            );
        }

        if (queryString != null) {
          isMounted &&
            sessionStorage.setItem(
              `${sessionStorageUniqueId}:queryString`,
              queryString,
            );
        }

        if (page != null) {
          isMounted &&
            sessionStorage.setItem(
              `${sessionStorageUniqueId}:page`,
              JSON.stringify(page),
            );
        }

        if (includeInactive != null) {
          isMounted &&
            sessionStorage.setItem(
              `${sessionStorageUniqueId}:includeInactive`,
              JSON.stringify(includeInactive),
            );
        }

        if (includePending != null) {
          isMounted &&
            sessionStorage.setItem(
              `${sessionStorageUniqueId}:includePending`,
              JSON.stringify(includePending),
            );
        }

        if (sessionFilters != null) {
          // Store filters external to core AKTable functionality
          for (const sessionFilter of sessionFilters) {
            if (sessionFilter.state !== undefined) {
              isMounted &&
                sessionStorage.setItem(
                  `${sessionStorageUniqueId}:${sessionFilter.key}`,
                  JSON.stringify(sessionFilter.state),
                );
            }
          }
        }
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    includeInactive,
    includePending,
    isAscending,
    orderKey,
    page,
    queryString,
    searchKeys,
    sessionLoaded,
    setSessionLoaded,
    sessionStorageUniqueId,
    setIncludePending,
    setIsAscending,
    setOrderKey,
    setPreviousIsAscending,
    setPreviousOrderKey,
    setPage,
    setQueryString,
    setSearchKeys,
    sessionFilters,
  ]);



  const getInlineButtonStyle = () => {
    switch (divisionNameForTheme) {
      case divisionTypes.PAINT:
        return " ak-edit-button-paint";
      case divisionTypes.CLEAN:
      case divisionTypes.HALLWAY_CLEAN:
        return " ak-edit-button-clean";
      case divisionTypes.SHAMPOO:
        return " ak-edit-button-shampoo";
      case divisionTypes.ALL:
        return " ak-edit-button-all";
      default:
        return " ak-edit-button";
    }
  };

  const getMainButtonStyle = () => {
    switch (divisionNameForTheme) {
      case divisionTypes.PAINT:
        return " ak-main-button-color-paint";
      case divisionTypes.CLEAN:
      case divisionTypes.HALLWAY_CLEAN:
        return " ak-main-button-color-clean";
      case divisionTypes.SHAMPOO:
        return " ak-main-button-color-shampoo";
      case divisionTypes.ALL:
        return " ak-main-button-color-all";
      default:
        return " ak-main-button-color";
    }
  };

  const hasCustomLineButtons =
    customLineButtons && customLineButtons.length > 0;

  const colorSelectedRows = (selectedRows, allRows) => {
    return (
      allRows &&
      allRows.map((d) => {
        let selectedRow = "ak-selected-row";

        let divisionKey = null;
        if (overrideDivisionKey) {
          divisionKey = overrideDivisionKey;
        } else if (d["division"]) {
          divisionKey = d["division"];
        }

        if (colorRowByDivision) {
          switch (divisionKey) {
            case divisionTypes.PAINT:
              selectedRow = "ak-selected-row-paint";
              break;
            case divisionTypes.SHAMPOO:
              selectedRow = "ak-selected-row-shampoo";
              break;
            case divisionTypes.CLEAN:
            case divisionTypes.HALLWAY_CLEAN:
              selectedRow = "ak-selected-row-clean";
              break;
            default:
              selectedRow = "ak-selected-row";
              break;
          }
        }

        if (selectedRows.find((m) => m.id === d.id)) {
          d._classes = `${d._classes}  ${selectedRow}`;
        }

        return d;
      })
    );
  };

  const colorWorkDaysRows = (workDaysRows, allRows) => {
    return (
      allRows &&
      allRows.map((d) => {
        let selectedRow = "ak-selected-row";

        let divisionKey = null;
        if (overrideDivisionKey) {
          divisionKey = overrideDivisionKey;
        } else if (d["division"]) {
          divisionKey = d["division"];
        }

        if (colorRowByDivision) {
          switch (divisionKey) {
            case divisionTypes.PAINT:
              selectedRow = "ak-work-days-row-paint";
              break;
            case divisionTypes.SHAMPOO:
              selectedRow = "ak-work-days-row-shampoo";
              break;
            case divisionTypes.CLEAN:
            case divisionTypes.HALLWAY_CLEAN:
              selectedRow = "ak-work-days-row-clean";
              break;
            default:
              selectedRow = "ak-work-days-row";
              break;
          }
        }

        if (workDaysRows.find((m) => m.id === d.id)) {
          if (selectedRows) {
            if (!selectedRows.find((n) => n.id === d.id)) {
              d._classes = `${d._classes}  ${selectedRow}`;
            }
          } else {
            d._classes = `${d._classes}  ${selectedRow}`;
          }
        }

        return d;
      })
    );
  };

  const colorStripedRows = (allRows) => {
    if (disabled) {
      return (
        allRows &&
        allRows.map((item, index) => {
          if (index % 2 === 0) {
            item._classes = "ak-disabled-dark-row";
          } else {
            item._classes = useLightAndDark
              ? "ak-disabled-light-row"
              : "ak-disabled-dark-row";
          }

          return item;
        })
      );
    }

    return (
      allRows &&
      allRows.map((item, index) => {
        let divisionKey = null;
        if (overrideDivisionKey) {
          divisionKey = overrideDivisionKey;
        } else if (item["division"]) {
          divisionKey = item["division"];
        }

        let selectableRowClass = "ak-selectable-row";
        let darkRowClass = "ak-dark-row";
        let lightRowClass = "ak-light-row";

        if (colorRowByDivision) {
          switch (divisionKey) {
            case divisionTypes.PAINT:
              selectableRowClass = "ak-selectable-row-paint";
              darkRowClass = "ak-dark-row-paint";
              lightRowClass = "ak-light-row-paint";
              break;
            case divisionTypes.SHAMPOO:
              selectableRowClass = "ak-selectable-row-shampoo";
              darkRowClass = "ak-dark-row-shampoo";
              lightRowClass = "ak-light-row-shampoo";
              break;
            case divisionTypes.ALL:
              selectableRowClass = "ak-selectable-row-all";
              darkRowClass = "ak-dark-row-all";
              lightRowClass = "ak-light-row-all";
              break;
            case divisionTypes.CLEAN:
            case divisionTypes.HALLWAY_CLEAN:
              selectableRowClass = "ak-selectable-row-clean";
              darkRowClass = "ak-dark-row-clean";
              lightRowClass = "ak-light-row-clean";
              break;
            default:
              selectableRowClass = "ak-selectable-row";
              darkRowClass = "ak-dark-row";
              lightRowClass = "ak-light-row";
              break;
          }
        }

        if (colorRowByDivision && overrideDivisionKey) {
          switch (overrideDivisionKey) {
            case divisionTypes.PAINT:
              selectableRowClass = "ak-selectable-row-paint";
              darkRowClass = "ak-dark-row-paint";
              lightRowClass = "ak-light-row-paint";
              break;
            case divisionTypes.SHAMPOO:
              selectableRowClass = "ak-selectable-row-shampoo";
              darkRowClass = "ak-dark-row-shampoo";
              lightRowClass = "ak-light-row-shampoo";
              break;
            case divisionTypes.CLEAN:
            case divisionTypes.HALLWAY_CLEAN:
              selectableRowClass = "ak-selectable-row-clean";
              darkRowClass = "ak-dark-row-clean";
              lightRowClass = "ak-light-row-clean";
              break;
            case divisionTypes.ALL:
              selectableRowClass = "ak-selectable-row-all";
              darkRowClass = "ak-dark-row-all";
              lightRowClass = "ak-light-row-all";
              break;
            default:
              selectableRowClass = "ak-selectable-row";
              darkRowClass = "ak-dark-row";
              lightRowClass = "ak-light-row";
              break;
          }
        }

        if (selectableRows) {
          item._classes = selectableRowClass;
        } else {
          item._classes = "";
        }

        if (index % 2 === 0) {
          if (item._classes.search(darkRowClass) === -1) {
            item._classes = `${item._classes} ${darkRowClass}`;
          }
        } else {
          if (item._classes.search(lightRowClass) === -1) {
            item._classes = useLightAndDark
              ? `${item._classes} ${lightRowClass}`
              : `${item._classes} ${darkRowClass}`;
          }
        }

        return item;
      })
    );
  };

  const addRowStyleClasses = (dataArray) => {
    let updatedData = colorStripedRows(dataArray);
    if (selectableRows && selectedRows) {
      updatedData = colorSelectedRows(selectedRows, updatedData);
    }
    if (workDaysRows) {
      updatedData = colorWorkDaysRows(workDaysRows, updatedData);
    }
    if (selectableRows && selectedRows) {
      if (forceSelectedRowUpdates) {
        // We need to set the data values matching any key from forceSelectedRowUpdates
        selectedRows.forEach((row) => {
          const index = updatedData.findIndex((x) => x.id === row.id);
          if (index >= 0) {
            forceSelectedRowUpdates.forEach((updateKey) => {
              if (row[updateKey]) {
                updatedData[index][updateKey] = row[updateKey];
              }
            });
          }
        });

        deselectedRows?.length > 0 &&
          deselectedRows.forEach((row) => {
            const index = updatedData.findIndex((x) => x.id === row.id);
            if (index >= 0) {
              forceSelectedRowUpdates.forEach((updateKey) => {
                if (row[updateKey] !== undefined) {
                  updatedData[index][updateKey] = row[updateKey];
                }
              });
            }
          });
      }
    }

    if (getRowClass) {
      updatedData = updatedData.map((item) => {
        const extraClass = getRowClass(item);
        if (extraClass) {
          item._classes = `${item._classes} ${extraClass}`;
        }
        return item;
      });
    }

    // CDataTable doesn't update even when copying array, need to force update with expensive
    // stringify/parse, still faster than forced update fetches however.
    return forceSelectedRowUpdates
      ? JSON.parse(JSON.stringify(updatedData))
      : updatedData;
  };

  const handleToggleIncludeInactive = () => {
    setIncludeInactive((oldState) => !oldState);
  };

  const handleToggleIncludePending = () => {
    setIncludePending((oldState) => !oldState);
  };

  const handleToggleSelectableRows = () => {
    setSelectableRows((oldState) => !oldState);
  };

  const handleInactiveSort = () => {
    let newIsAscending;

    if (orderKey === "isActive") {
      newIsAscending = !isAscending;
    } else {
      newIsAscending = true;
    }

    onSortClick(newIsAscending, "isActive");
  };

  const handleSort = (option, overrideAscending) => {
    let newIsAscending;

    if (overrideAscending !== undefined) {
      newIsAscending = overrideAscending;
    } else if (orderKey === option.key) {
      newIsAscending = !isAscending;
    } else {
      newIsAscending = true;
    }

    let sortedData;

    if (!option.sortMethod && onSortClick) {
      onSortClick(newIsAscending, option.key);
      return;
    } else {
      if (setPreviousIsAscending) {
        setPreviousIsAscending(isAscending);
      }
      if (setPreviousOrderKey) {
        setPreviousOrderKey(orderKey);
      }
      setIsAscending(newIsAscending);
      setOrderKey(option.key);

      switch (option.sortMethod) {
        case optionSortMethods.STRING:
          sortedData = data.sort((a, b) => {
            if (a[option.key] === b[option.key]) {
              return 0;
            } else if (a[option.key] === null || a[option.key] === undefined) {
              return 1;
            } else if (b[option.key] === null || b[option.key] === undefined) {
              return -1;
            } else if (newIsAscending) {
              if (
                Array.isArray(a[option.key]) &&
                Array.isArray(b[option.key])
              ) {
                return a[option.key][0]?.toUpperCase() >
                  b[option.key][0]?.toUpperCase()
                  ? 1
                  : -1;
              } else {
                return a[option.key]?.toUpperCase() >
                  b[option.key]?.toUpperCase()
                  ? 1
                  : -1;
              }
            } else {
              if (
                Array.isArray(a[option.key]) &&
                Array.isArray(b[option.key])
              ) {
                return a[option.key][0]?.toUpperCase() <
                  b[option.key][0]?.toUpperCase()
                  ? 1
                  : -1;
              } else {
                return a[option.key]?.toUpperCase() <
                  b[option.key]?.toUpperCase()
                  ? 1
                  : -1;
              }
            }
          });
          break;
        case optionSortMethods.NUMBER:
          sortedData = data.sort((a, b) => {
            if (a[option.key] === b[option.key]) {
              return 0;
            } else if (a[option.key] === null || a[option.key] === undefined) {
              return 1;
            } else if (b[option.key] === null || b[option.key] === undefined) {
              return -1;
            } else if (newIsAscending) {
              return parseFloat(a[option.key]) > parseFloat(b[option.key])
                ? 1
                : -1;
            } else {
              return parseFloat(a[option.key]) < parseFloat(b[option.key])
                ? 1
                : -1;
            }
          });
          break;
        case optionSortMethods.DATE:
          sortedData = data.sort((a, b) => {
            if (a[option.key] === b[option.key]) {
              return 0;
            } else if (a[option.key] === null || a[option.key] === undefined) {
              return 1;
            } else if (b[option.key] === null || b[option.key] === undefined) {
              return -1;
            } else if (newIsAscending) {
              return new Date(a[option.key]) - new Date(b[option.key]);
            } else {
              return new Date(b[option.key]) - new Date(a[option.key]);
            }
          });
          break;
        case optionSortMethods.BOOLEAN:
          if (newIsAscending) {
            sortedData = data.sort((a, b) => {
              return b[option.key] - a[option.key];
            });
          } else {
            sortedData = data.sort((a, b) => {
              return a[option.key] - b[option.key];
            });
          }
          break;
        default:
          if (typeof option.sortMethod == "function") {
            sortedData = data.sort((a, b) =>
              option.sortMethod(newIsAscending, a[option.key], b[option.key]),
            );
          }
          break;
      }
    }

    internalUpdate.current = true;
    if (Array.isArray(sortedData)) {
      setData([...sortedData]);
    } else {
      setData(sortedData);
    }
  };

  const handleCustomLineButtonClick = (item, clickHandler, index) => {
    clickHandler(item, index);
  };

  const handleColumnOptionChange = (event, index) => {
    if (setOptions) {
      const newOptions = [...options];
      newOptions[index].show = event.target.checked;
      setOptions(newOptions);
    }
  };

  const handleResetVisibleColumns = () => {
    if (setOptions) {
      const newOptions = options.map((option) => {
        option.show = option.default === true;
        return option;
      });
      setOptions(newOptions);
    }
  };

  const fields = [];
  const columnHeaderSlot = {};
  const scopedSlots = {
    lineButtonCol: (item, index) => {
      if (hasCustomLineButtons) {
        return (
          <td
            className={
              item.isActive === false &&
              (item.isPending === false || item.isPending === null)
                ? "inactive-item ak-button-cell"
                : item.isPending === true
                  ? "pending-item ak-button-cell"
                  : "ak-button-cell"
            }
          >
            <div
              className={
                wrapCustomButtons
                  ? "ak-button-container ak-button-container-wrap"
                  : customLineButtons.length <= 2
                    ? "ak-button-container justify-content-center"
                    : "ak-button-container"
              }
            >
              {(!selectableRows || (selectableRows && alwaysShowLineButtons)) &&
                customLineButtons.map((customButton) => {
                  let hideButton = false;
                  if (customButton.showFor && customButton.showFor.length > 0) {
                    hideButton = true;
                    customButton.showFor.forEach((keyVal) => {
                      if (item[keyVal.key] === keyVal.val) {
                        hideButton = false;
                      }
                    });
                  }

                  if (customButton.hideFor && customButton.hideFor.length > 0) {
                    customButton.hideFor.forEach((keyVal) => {
                      if (item[keyVal.key] === keyVal.val) {
                        hideButton = true;
                      }
                    });
                  }

                  return (
                    !hideButton && (
                      <button
                        type="button"
                        style={{
                          minWidth: `${
                            customButton.minWidth
                              ? `${customButton.minWidth}rem`
                              : ""
                          }`,
                        }}
                        key={customButton.buttonLabel}
                        className={`${getInlineButtonStyle()} text-nowrap`}
                        onClick={() =>
                          handleCustomLineButtonClick(
                            item,
                            customButton.onClick,
                            index,
                          )
                        }
                      >
                        {customButton.buttonLabel}
                      </button>
                    )
                  );
                })}
            </div>
          </td>
        );
      } else {
        return <>;</>;
      }
    },
  };

  let index = 0;

  for (const option of options) {
    const key = option.key;

    if (option.show) {
      fields.push({
        key: option.key,
        label: option.header,
        _style: { ...option._style },
      });
    }

    columnHeaderSlot[key] = (
      <AKHeader
        divisionNameForTheme={divisionNameForTheme}
        key={index}
        option={option}
        handleClick={handleSort}
        orderKey={orderKey}
        isAscending={isAscending}
        index={index}
        disabled={disabled}
        disableSorting={disableSorting}
      />
    );
    scopedSlots[key] = (item, index) => {
      item.index = index;
      return (
        <AKCell
          option={option}
          key={index}
          type={option.type}
          typeStyle={option.typeStyle}
          item={item}
          itemKey={key}
          onInlineEdit={onInlineEdit}
          onInlineValidate={onInlineValidate}
          onInlineFormat={onInlineFormat}
          inlineSelections={inlineSelections}
          ignoreInlineKeys={ignoreInlineKeys}
          customInlineKeys={customInlineKeys}
          setInlineSelections={setInlineSelections}
          onCellClick={onCellClick}
          inlineCellValidation={inlineCellValidation}
          validCells={validCells}
          lockedRows={lockedRows}
          lockedCells={lockedCells}
          emphasizedCells={emphasizedCells}
          handleShouldLockCell={handleShouldLockCell}
          handleShouldEmphasizeCell={handleShouldEmphasizeCell}
          forceRerender={forceRerender}
          setForceRerender={setForceRerender}
          {...cellProps}
        />
      );
    };

    index++;
  }

  if (
    hasCustomLineButtons &&
    (!selectableRows || (selectableRows && alwaysShowLineButtons))
  ) {
    fields.push({ key: "lineButtonCol", label: "" });
    columnHeaderSlot["lineButtonCol"] = (
      <AKHeader
        divisionNameForTheme={divisionNameForTheme}
        key={options.length - 1}
        index={options.length - 1}
      />
    );

    if (includeInactive && !setIncludePending) {
      columnHeaderSlot["lineButtonCol"] = (
        <button
          className={`${
            divisionNameForTheme && allDivisions.includes(divisionNameForTheme)
              ? `header-button-${divisionNameForTheme.toLowerCase()}`
              : "header-button-all"
          }`}
          type="button"
          onClick={handleInactiveSort}
        >
          Inactive
          <i
            className={
              orderKey === "isActive"
                ? isAscending
                  ? "fa fa-caret-down fa-lg"
                  : "fa fa-caret-up fa-lg"
                : "fa fa-caret-right fa-lg"
            }
          />
        </button>
      );
    }
  }

  /**
   * Handles clearing session storage
   */
  const handleClearSessionStorage = (event) => {
    event.preventDefault();

    let n = sessionStorage.length;

    while (n--) {
      const key = sessionStorage.key(n);
      const keyString = key.toString();

      if (keyString.includes(sessionStorageUniqueId)) {
        sessionStorage.removeItem(key);
      }
    }
    window.location.reload();
  };

  const dataTable = useMemo(() => {
    return (
      <CDataTable
        items={addRowStyleClasses(data)}
        onRowClick={selectableRows && onRowClick ? onRowClick : null}
        fields={fields}
        columnHeaderSlot={columnHeaderSlot}
        scopedSlots={scopedSlots}
      />
    );
  }, [data, selectedRows, options]);

  return (
    <>
      <div
        className="ak-upper-container row w-100"
        style={minWidth ? { minWidth: minWidth } : null}
      >
        <div className="ak-add-button-container col-12 col-md-6">
          {onAddClick && (
            <button
              type="button"
              className={`ak-add-button ${getMainButtonStyle()} mr-2`}
              onClick={(event) => onAddClick(data)}
            >
              Add
            </button>
          )}
          {onImportClick && (
            <button
              type="button"
              className={`ak-import-button ${getMainButtonStyle()} mr-2`}
              onClick={onImportClick}
            >
              Import
            </button>
          )}
          {customHeaderButton && (
            <button
              type="button"
              className={`ak-custom-button ${getMainButtonStyle()} mr-2`}
              onClick={() => {
                customHeaderButton.handleClick(data);
              }}
            >
              {customHeaderButton.label}
            </button>
          )}
          {handleExportDataClick && (
            <button
              type="button"
              className={`ak-custom-button ${getMainButtonStyle()} mr-2`}
              onClick={handleExportDataClick}
            >
              Export
            </button>
          )}
          {sessionStorageUniqueId && (
            <button
              type="button"
              className={`ak-custom-button ${getMainButtonStyle()} mr-2`}
              onClick={handleClearSessionStorage}
            >
              Reset Filters
            </button>
          )}
        </div>
        <div className="search-container col-12 col-md-6 pr-0">
          {employeeManagement && (
            <button
              type="button"
              className={`${getMainButtonStyle()} pl-2 pr-2 mr-2 h-100`}
              onClick={onEmailSetInactivesClick}
              title={"Email and Set Inactive Users"}
            >
              Process Inactives
            </button>
          )}
          {setOptions && (
            <button
              type="button"
              className={`${getMainButtonStyle()} pl-2 pr-2 mr-2 h-100`}
              onClick={() => {
                setShowColumnModal(true);
              }}
              title={"Adjust Visible Columns"}
            >
              <CIcon name="cil-library" />
            </button>
          )}
          {onSearchClick && (
            <AKSearchBox
              queryString={queryString}
              setQueryString={setQueryString}
              searchKeys={searchKeys}
              setSearchKeys={setSearchKeys}
              onSearchClick={onSearchClick}
              searchButtonStyle={getMainButtonStyle()}
              options={options}
            />
          )}
        </div>
      </div>
      <InfiniteScroller
        useInfiniteScroll={useInfiniteScroll}
        numItems={numItems}
        rowHeight={rowHeight}
        tableHeight={tableHeight}
        batchSize={batchSize}
        fetchBuffer={fetchBuffer}
        handleFetchData={handleFetchData}
        shouldResetState={shouldResetInfiniteScrollingState}
        setShouldResetState={setShouldResetInfiniteScrollingState}
      >
        <div
          className={`pt-0 ak-flex-container ${
            numberOfPages <= 1 && "pb-0 mb-0"
          }`}
          style={minWidth ? { minWidth: minWidth } : { minWidth: "772px" }}
        >
          <div className="ak-table-container">
            <>
              {(numberOfPages > 0 && data?.length > 0) || loading ? (
                <div
                  className={`${
                    useInfiniteScroll
                      ? "ak-datatable-container-batched-fetch"
                      : "ak-datatable-container"
                  } ${datatableContainerClassName}`}
                  style={maxHeight && { maxHeight: maxHeight }}
                  ref={ref}
                >
                  <div
                    className="ak-loading-overlay"
                    style={{
                      height: ref && ref.current && ref.current.clientHeight,
                      width: ref && ref.current && ref.current.clientWidth,
                      display: loading ? "flex" : "none",
                    }}
                  >
                    <Loading
                      style={
                        numberOfPages > 0 && data?.length > 0
                          ? { margin: 0 }
                          : { marginLeft: "45rem", marginTop: "10rem" }
                      }
                    />
                  </div>
                  {title && (
                    <label
                      className={
                        titleClassName
                          ? `${titleClassName} font-weight-bold text-center mb-2 w-100`
                          : "font-weight-bold text-center mb-2 w-100"
                      }
                    >
                      {title}
                    </label>
                  )}
                  {numberOfPages > 0 && data?.length > 0 && dataTable}
                </div>
              ) : (
                <h3 className="text-center pb-4">
                  {noResultsMessage ? noResultsMessage : "No Results Found"}
                </h3>
              )}
            </>
          </div>
          <CModal
            show={showColumnModal}
            closeOnBackdrop={false}
            onClose={() => {
              setShowColumnModal(false);
            }}
            size=""
          >
            <CModalHeader>
              <CModalTitle>Visible Columns</CModalTitle>
            </CModalHeader>
            <CModalBody>
              <div className="m-auto">
                {data &&
                  options.map((option, index) => {
                    return (
                      <FormInput
                        key={index}
                        inputsPerRow={2}
                        id={option.key}
                        inputWidth={12}
                        label={option.header}
                        onChange={(event) =>
                          handleColumnOptionChange(event, index)
                        }
                        value={option.show === true}
                        type={inputTypes.CHECKBOX}
                        editable
                        noTopMargin
                      />
                    );
                  })}
              </div>
            </CModalBody>
            <CModalFooter>
              <CButton
                className={`ak-checkbox-button`}
                onClick={handleResetVisibleColumns}
              >
                Reset
              </CButton>
              <CButton
                className={`ak-checkbox-button`}
                onClick={() => {
                  setShowColumnModal(false);
                }}
              >
                Done
              </CButton>
            </CModalFooter>
          </CModal>
        </div>
      </InfiniteScroller>
      <div
        className={`${
          includeInactive !== undefined && setIncludeInactive
            ? "ak-table-footer"
            : "ak-table-footer-right"
        } ${numberOfPages <= 1 && "pt-0 pb-0"}`}
      >
        {includeInactive !== undefined && setIncludeInactive && (
          <div className="include-inactive-container">
            <label className="ak-checkbox-label">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={handleToggleIncludeInactive}
              />
              <span className="ak-checkbox-custom rectangular"></span>
            </label>
            <h5 className="include-inactive-title">Include Inactive</h5>
            {includePending !== undefined && setIncludePending && (
              <div className="include-pending-container">
                <label className="ak-checkbox-label">
                  <input
                    type="checkbox"
                    checked={includePending}
                    onChange={handleToggleIncludePending}
                  />
                  <span className="ak-checkbox-custom rectangular"></span>
                </label>
                <h5 className="include-inactive-title">Include Pending</h5>
              </div>
            )}
            {!hasCustomLineButtons && includeInactive && (
              <button
                type="button"
                className="btn"
                onClick={handleInactiveSort}
              >
                <h5 className="sort-inactive-title">
                  Sort Inactive{" "}
                  <i
                    className={
                      orderKey === "isActive"
                        ? isAscending
                          ? "fa fa-caret-down fa-lg"
                          : "fa fa-caret-up fa-lg"
                        : "fa fa-caret-right fa-lg"
                    }
                  />
                </h5>
              </button>
            )}
          </div>
        )}
        {selectableRows !== undefined &&
          setSelectableRows &&
          onRowClick &&
          !hideRowSelectionToggle && (
            <div className="include-inactive-container">
              <label className="ak-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectableRows}
                  onChange={handleToggleSelectableRows}
                />
                <span className="ak-checkbox-custom rectangular"></span>
              </label>
              <h6 className="include-inactive-title pl-2">
                {rowSelectionTitle ? rowSelectionTitle : "Enable Row Selection"}
              </h6>
            </div>
          )}
        {page && setPage && numberOfPages > 1 && (
          <div className="ml-3">
            <AKPaginationButtons
              divisionNameForTheme={divisionNameForTheme}
              page={page}
              setPage={setPage}
              numberOfPages={numberOfPages}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(AKTable);
